import { forwardRef, ReactNode, Ref } from 'react';
import {
  composeRenderProps,
  InputProps,
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  ValidationResult,
} from 'react-aria-components';
import { composeTailwindRenderProps } from 'src/utils';

import {
  Description,
  FieldError,
  Input,
  TextArea,
  Label,
  iconStyles,
  textareaStyles,
  inputStyles,
  hintStyles,
} from './Field';

import type { ColorVariant, IconProps, Size } from 'src/types/common';

export interface TextFieldProps extends AriaTextFieldProps {
  variant?: ColorVariant;
  label?: string;
  placeholder?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  size?: Size;
  Icon?: IconProps;
  rightSlot?: ReactNode;
  rows?: number;
  inputProps?: InputProps;
}

const TextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextFieldProps
>(
  (
    {
      variant = 'surface',
      size = 'medium',
      Icon,
      rightSlot,
      label,
      description,
      errorMessage,
      className,
      placeholder,
      rows,
      inputProps,
      ...props
    },
    ref
  ) => {
    const isSmall = size === 'small';
    const isTextArea = Boolean(rows);

    return (
      <AriaTextField
        {...props}
        className={composeTailwindRenderProps(className, 'flex flex-col gap-1')}
      >
        {label && (
          <Label
            isRequired={props.isRequired}
            className={size === 'small' ? 'text-md' : 'text-base'}
          >
            {label}
          </Label>
        )}
        <div className="relative">
          {!!Icon && (
            <Icon
              aria-hidden="true"
              className={`pointer-events-none ${isSmall ? 'left-2' : 'left-3'} ${iconStyles({ size, isTextArea })}`}
            />
          )}
          {isTextArea ? (
            <TextArea
              ref={ref as Ref<HTMLTextAreaElement>}
              rows={rows}
              placeholder={placeholder}
              className={composeRenderProps('', (cn, renderProps) =>
                textareaStyles({
                  ...renderProps,
                  className: cn,
                  variant,
                  size,
                  hasIcon: !!Icon,
                  rightSlotActive: !!rightSlot,
                })
              )}
            />
          ) : (
            <Input
              ref={ref as Ref<HTMLInputElement>}
              placeholder={placeholder}
              {...inputProps}
              className={composeRenderProps('', (cn, renderProps) =>
                inputStyles({
                  ...renderProps,
                  className: cn,
                  variant,
                  size,
                  hasIcon: !!Icon,
                  rightSlotActive: !!rightSlot,
                })
              )}
            />
          )}
          {rightSlot}
        </div>
        {description && (
          <Description className={hintStyles({ variant })}>
            {description}
          </Description>
        )}
        <FieldError>{errorMessage}</FieldError>
      </AriaTextField>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
